'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { useT } from '@/components/i18n-provider';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptic';
import { Avatar } from '@/components/avatar';
import { ChatIcon } from '@/components/icons/chat-icon';
import { useAuth } from '@/components/auth-provider';
import { useChatSidebar } from '@/components/chat-sidebar-provider';
import { ExternalLink } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Tip } from '@/components/ui/tooltip';

interface AgentOption {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  accountType: 'human' | 'agent';
}

// Module-level cache for user agents
let cachedAgents: AgentOption[] | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60_000;

async function fetchUserAgents(): Promise<AgentOption[]> {
  if (cachedAgents && Date.now() - cacheTimestamp < CACHE_TTL) return cachedAgents;
  try {
    const res = await fetch('/api/agents');
    if (!res.ok) return cachedAgents || [];
    const data = await res.json();
    cachedAgents = ([...(data.agents || [])].reverse().slice(0, 3)).map((a: Record<string, unknown>) => ({
      id: a.id as string,
      username: a.username as string,
      displayName: (a.displayName || a.username) as string,
      avatarUrl: a.avatarUrl as string | null,
      accountType: 'agent' as const,
    }));
    cacheTimestamp = Date.now();
    return cachedAgents!;
  } catch {
    return cachedAgents || [];
  }
}

export interface TaskCTAMenuProps {
  postId: string;
  authorUsername: string;
  authorAccountType: 'human' | 'agent';
  chatCta?: string | null;
  /** Show "Task" label next to icon */
  showLabel?: boolean;
  /** Additional class for the trigger button */
  className?: string;
  /** Icon size class (default h-4 w-4) */
  iconSize?: string;
  /** Padding class for the icon wrapper (default p-1.5) */
  iconPadding?: string;
  /** Called after selecting an agent (e.g., close lightbox) */
  onAfterSelect?: () => void;
}

export function TaskCTAMenu({
  postId,
  authorUsername,
  authorAccountType,
  chatCta,
  showLabel = true,
  className,
  iconSize = 'h-4 w-4',
  iconPadding = 'p-1.5',
  onAfterSelect,
}: TaskCTAMenuProps) {
  const t = useT();
  const { user } = useAuth();
  const router = useRouter();
  const chatSidebar = useChatSidebar();
  const [open, setOpen] = useState(false);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [loading, setLoading] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const isAuthorAgent = authorAccountType === 'agent';

  // Position the dropdown above the trigger button
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.right });
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const handleOpen = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('light');
    if (!user) { router.push('/login'); return; }
    setOpen(true);
    setLoading(true);
    const userAgents = await fetchUserAgents();
    setAgents(userAgents);
    setLoading(false);
  }, [user, router]);

  const handleSelect = useCallback((agent: AgentOption) => {
    setOpen(false);
    let prefillText = '';
    if (postId) {
      const postUrl = `${window.location.origin}/p/${postId}`;
      const isPostAuthor = agent.username === authorUsername;
      prefillText = isPostAuthor
        ? (chatCta ? `${chatCta} ${postUrl}` : `${t('posts.learnMorePrompt')} ${postUrl}`)
        : `${t('posts.checkWithAuthor', { username: authorUsername })}${postUrl}`;
    }
    chatSidebar.openDraftChat({
      participantUsername: agent.username,
      prefillText,
    });
    onAfterSelect?.();
  }, [postId, authorUsername, chatSidebar, onAfterSelect, t]);

  const dropdown = open && pos && createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-[9999] w-52 bg-popover border border-border rounded-lg shadow-lg py-1 text-sm"
      style={{ top: pos.top, left: pos.left, transform: 'translate(-100%, -100%) translateY(-4px)' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50">
        <span className="text-xs font-medium text-muted-foreground">{t('messages.sendToAgent')}</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-3">
          <Spinner size="md" tone="muted" />
        </div>
      ) : (
        <>
          {isAuthorAgent && (
            <button
              onClick={() => handleSelect({ id: '', username: authorUsername, displayName: authorUsername, avatarUrl: null, accountType: 'agent' })}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors"
            >
              <Avatar seed={authorUsername} type="agent" className="h-6 w-6" />
              <span className="truncate">@{authorUsername}</span>
              <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-500/15 text-purple-500">{t('posts.author')}</span>
            </button>
          )}

          {agents.length > 0 && (
            <>
              {isAuthorAgent && <div className="border-t border-border/50 my-0.5" />}
              {agents.map((agent) => (
                <button
                  key={agent.id}
                  onClick={() => handleSelect(agent)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors"
                >
                  <Avatar seed={agent.username} type="agent" src={agent.avatarUrl} className="h-6 w-6" />
                  <span className="truncate">@{agent.username}</span>
                </button>
              ))}
            </>
          )}

          {!isAuthorAgent && agents.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              <p className="mb-1.5">{t('agents.noAgents')}</p>
              <a
                href="/create?tab=agent"
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                {t('actions.createAgent')} <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
        </>
      )}
    </div>,
    document.body,
  );

  return (
    <div className={cn("relative", className)} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <Tip label={t('posts.chat')}>
      <button
        ref={triggerRef}
        onClick={handleOpen}
        className={cn(
          'group/task flex items-center transition-colors',
          isAuthorAgent ? 'text-purple-500' : 'text-muted-foreground hover:text-purple-500',
        )}
      >
        {showLabel && <span className="text-xs font-medium mr-0.5">{t('posts.chat')}</span>}
        <div className={cn('relative rounded-full group-hover/task:bg-purple-500/10 transition-colors', iconPadding)}>
          <ChatIcon className={iconSize} />
        </div>
      </button>
      </Tip>
      {dropdown}
    </div>
  );
}
